import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { fairLendingApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';

interface FldDrillDownModalProps {
  analysisId: string;
  riskFactor: string;
  /** 'SUBORDINATE' when the cell is in a subordinate lien table. */
  lienVariant?: 'SUBORDINATE';
  /** The open-end side of a split table (2026-10-23). */
  openEndVariant?: 'NOT_OPEN_END' | 'OPEN_END';
  /** The Lending Channel of a Pricing table (2026-11-01). */
  channelVariant?: 'PORTFOLIO' | 'SECONDARY_MARKET';
  cell: {
    protectedGroup: string;
    loanPurpose: string;
    count: number;
    controlCount: number;
  };
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  actionTaken: 'Action',
  applicantRace1: 'Race',
  applicantEthnicity1: 'Ethnicity',
  applicantSex: 'Sex',
  loanAmount: 'Loan Amt',
  income: 'Income',
  interestRate: 'Int. Rate',
  rateSpread: 'Rate Spread',
  censusTract: 'Census Tract',
};

export default function FldDrillDownModal({
  analysisId,
  riskFactor,
  lienVariant,
  openEndVariant,
  channelVariant,
  cell,
  onClose,
}: FldDrillDownModalProps) {
  const { teamId } = useTeamId();
  const [viewGroup, setViewGroup] = useState<'pbg' | 'control'>('pbg');

  const { data, isLoading } = useQuery({
    queryKey: ['fld-drill-down', teamId, analysisId, riskFactor, lienVariant ?? 'FIRST', openEndVariant ?? 'UNSPLIT', channelVariant ?? 'ALL', cell.protectedGroup, cell.loanPurpose],
    queryFn: async () => {
      const res = await fairLendingApi.getFldDrillDown(teamId!, analysisId, {
        riskFactor,
        protectedGroup: cell.protectedGroup,
        loanPurpose: cell.loanPurpose,
        lienVariant,
        openEndVariant,
        channelVariant,
      });
      return res.data;
    },
    enabled: !!teamId,
  });

  const records = viewGroup === 'pbg' ? data?.pbgRecords : data?.controlRecords;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-ink/40" onClick={onClose} />
        <div className="relative transform overflow-hidden rounded-lg bg-panel text-left shadow-xl sm:my-8 sm:w-full sm:max-w-4xl">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h3 className="text-lg font-semibold text-ink">Drill-Down: {cell.protectedGroup}</h3>
              <p className="text-sm text-ink-3">{cell.loanPurpose} — {riskFactor}</p>
            </div>
            <button onClick={onClose} className="text-ink-4 hover:text-ink-3">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-3 border-b bg-panel-2">
            <div className="flex gap-2">
              <button
                onClick={() => setViewGroup('pbg')}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  viewGroup === 'pbg'
                    ? 'bg-teal-600 text-white'
                    : 'bg-panel text-ink-2 border hover:bg-panel-2'
                }`}
              >
                Protected Group ({cell.count})
              </button>
              <button
                onClick={() => setViewGroup('control')}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  viewGroup === 'control'
                    ? 'bg-teal-600 text-white'
                    : 'bg-panel text-ink-2 border hover:bg-panel-2'
                }`}
              >
                Control Group ({data?.controlRecords?.length ?? cell.controlCount})
              </button>
            </div>
          </div>

          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="animate-pulse space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-8 bg-panel-2 rounded" />
                ))}
              </div>
            ) : !records || records.length === 0 ? (
              <p className="text-ink-3 text-sm text-center py-8">No records found.</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    {Object.entries(FIELD_LABELS).map(([key, label]) => (
                      <th key={key} className="text-left py-2 px-2 text-xs font-medium text-ink-3">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record: any, i: number) => (
                    <tr key={record.id || i} className="border-b border-rule hover:bg-panel-2">
                      {Object.keys(FIELD_LABELS).map(key => (
                        <td key={key} className="py-1.5 px-2 text-xs text-ink-2">
                          {record[key] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="px-6 py-3 border-t bg-panel-2 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-ink-2 bg-panel border rounded-md hover:bg-panel-2"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
